import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Editor } from "primereact/editor";
import { categoryService } from "../../../services/api/categoryService";
import { firebaseService } from "../../../services/api/firebaseService";
import CustomInput from "../components/CustomInput";
import Loading from "../components/Loading";
import Modal from "../components/Modal";

const AddCategory = ({
  isOpen,
  onClose,
  token,
  onSuccess,
  mode = "add", // "add" | "edit"
  editId = null,
}) => {
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [imgPreview, setImgPreview] = useState("");
  const [imgFile, setImgFile] = useState(null);

  const [serviceData, setServiceData] = useState({
    name: "",
    detail: "",
  });

  const [oldImageUrl, setOldImageUrl] = useState(""); // Track old logo for cleanup
  const [oldContentImages, setOldContentImages] = useState([]); // Track old images in content
  const [detailLoading, setDetailLoading] = useState(false);

  /** Extract Firebase image URLs from HTML content */
  const extractFirebaseImageUrls = (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== "string") return [];

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img");

    const firebaseUrls = [];
    images.forEach((img) => {
      const src = img.getAttribute("src");
      if (src && (src.startsWith("https://storage.googleapis.com/") || src.startsWith("https://firebasestorage.googleapis.com/"))) {
        firebaseUrls.push(src);
      }
    });

    return firebaseUrls;
  };

  /** Process images in HTML content and upload to Firebase */
  const processImagesInContent = async (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== "string" || htmlContent === "<p><br></p>") {
      return htmlContent || "";
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img");

    if (images.length === 0) {
      return htmlContent;
    }

    // Process each image
    for (const img of images) {
      const src = img.getAttribute("src");

      // Skip if already a Firebase URL
      if (!src || src.startsWith("https://storage.googleapis.com/") || src.startsWith("https://firebasestorage.googleapis.com/")) {
        continue;
      }

      // Check if it's a base64 image
      if (src.startsWith("data:image/")) {
        try {
          // Convert base64 to File
          const fetchRes = await fetch(src);
          const blob = await fetchRes.blob();
          const fileName = `category-editor-${Date.now()}.png`;
          const file = new File([blob], fileName, { type: blob.type });

          // Upload to Firebase
          const uploadRes = await firebaseService.upload_file(token, file, "category-content");
          const uploadedUrl = uploadRes?.url;

          if (!uploadedUrl) {
            throw new Error("Upload ảnh thất bại");
          }

          // Replace src with Firebase URL
          img.setAttribute("src", uploadedUrl);
        } catch (error) {
          console.error("Error uploading image:", error);
          toast.error(`Không thể upload ảnh trong nội dung: ${error.message}`);
          throw error;
        }
      }
    }

    // Return processed HTML
    return doc.body.innerHTML;
  };

  const fetchCategoryDetail = useCallback(
    async (id) => {
      if (!id) return;

      try {
        setDetailLoading(true);

        const res = await categoryService.get_by_id(id, token);

        setServiceData({
          name: res?.name || "",
          detail: res?.description || "",
        });

        const currentImage = res?.image || "";
        setImgPreview(currentImage);
        setOldImageUrl(currentImage);

        // Track images in current description
        setOldContentImages(extractFirebaseImageUrls(res?.description || ""));

        setImgFile(null);
      } catch (error) {
        console.error(error);
        toast.error(
          error?.response?.data?.message ||
          "Không thể tải chi tiết loại dịch vụ",
        );
      } finally {
        setDetailLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    if (!isOpen) return;

    if (mode === "edit") {
      if (editId) fetchCategoryDetail(editId);
      return;
    }

    // mode add
    setServiceData({ name: "", detail: "" });
    setImgPreview("");
    setOldImageUrl("");
    setOldContentImages([]);
    setImgFile(null);
    setErrors({});
  }, [isOpen, mode, editId, fetchCategoryDetail]);

  /* ===================== IMAGE SELECT ===================== */
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Chỉ chấp nhận định dạng JPG / PNG / WEBP");
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      toast.error("Kích thước ảnh vượt quá 25MB");
      return;
    }

    setImgFile(file);
    setImgPreview(URL.createObjectURL(file));
  };

  /* ===================== INPUT ===================== */
  const handleInputChange = (key, value) => {
    setServiceData((prev) => ({ ...prev, [key]: value }));
  };

  /* ===================== VALIDATE ===================== */
  const validateFields = () => {
    const tempErrors = {};
    if (!serviceData.name.trim())
      tempErrors.name = "Tên loại dịch vụ không được để trống";
    if (!serviceData.detail || serviceData.detail.trim() === "<p><br></p>")
      tempErrors.detail = "Mô tả không được để trống";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  /* ===================== SUBMIT ===================== */
  const handleFormSubmit = async () => {
    if (!validateFields()) return;

    try {
      setLoading(true);

      // 1. Upload logo if changed
      let imageUrl = imgPreview || "";
      if (imgFile) {
        const uploadRes = await firebaseService.upload_file(
          token,
          imgFile,
          "category-images",
        );

        if (!uploadRes?.url) {
          toast.error("Upload hình ảnh thất bại");
          return;
        }

        imageUrl = uploadRes.url;
      }

      // 2. Process images in description content
      const processedDetail = await processImagesInContent(serviceData.detail);
      const newContentImages = extractFirebaseImageUrls(processedDetail);

      const payload = {
        categoryName: serviceData.name,
        description: processedDetail,
        image: imageUrl || undefined,
      };

      if (mode === "edit" && editId) {
        await categoryService.update_category(editId, payload, token);

        // 3. Cleanup old logo
        if (
          imgFile &&
          oldImageUrl &&
          (oldImageUrl.startsWith("https://storage.googleapis.com/") || oldImageUrl.startsWith("https://firebasestorage.googleapis.com/"))
        ) {
          try {
            await firebaseService.delete_file(token, oldImageUrl);
          } catch (err) {
            console.warn("Failed to delete old logo:", err);
          }
        }

        // 4. Cleanup old images in content that are no longer present
        if (oldContentImages.length > 0) {
          const imagesToDelete = oldContentImages.filter(url => !newContentImages.includes(url));
          for (const url of imagesToDelete) {
            try {
              await firebaseService.delete_file(token, url);
            } catch (err) {
              console.warn("Failed to delete orphaned image:", url, err);
            }
          }
        }

        toast.success("Cập nhật loại dịch vụ thành công");
      } else {
        await categoryService.create_category(payload, token);
        toast.success("Tạo loại dịch vụ mới thành công");
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message || "Không thể kết nối tới server",
      );
    } finally {
      setLoading(false);
    }
  };

  /* ===================== UI ===================== */
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        mode === "edit" ? "Chỉnh sửa loại dịch vụ" : "Thêm loại dịch vụ mới"
      }
      primaryButtonText={
        loading ? "Đang lưu..." : mode === "edit" ? "Cập nhật" : "Thêm"
      }
      maxWidth="850px"
      cancelButtonText="Hủy"
      showCancelButton
      primaryButtonOnClick={loading ? null : handleFormSubmit}
    >
      {loading || detailLoading ? (
        <div className="flex justify-center py-10 min-h-[400px]">
          <Loading />
        </div>
      ) : (
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* IMAGE */}
          <div>
            <strong className="text-gray-700 block mb-2">
              Hình ảnh loại dịch vụ
            </strong>
            <label className="cursor-pointer w-28 h-28 bg-gray-100 flex items-center justify-center rounded-md overflow-hidden hover:opacity-80 border-2 border-dashed border-gray-300">
              {imgPreview ? (
                <img
                  src={imgPreview}
                  alt="preview"
                  className="w-full h-full object-cover object-center shrink-0"
                />
              ) : (
                <img src="/icons/camera-icon.svg" alt="upload" />
              )}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
            </label>
          </div>

          {/* NAME */}
          <div>
            <strong className="text-gray-700 block mb-1">
              Tên loại dịch vụ
            </strong>
            <CustomInput
              value={serviceData.name}
              placeholder="Nhập tên loại dịch vụ"
              onChange={(v) => handleInputChange("name", v)}
              error={errors.name}
            />
          </div>

          {/* DESCRIPTION */}
          <div>
            <strong className="text-gray-700 block mb-1">
              Mô tả loại dịch vụ
            </strong>
            <Editor
              value={serviceData.detail}
              onTextChange={(e) => handleInputChange("detail", e.htmlValue)}
              style={{ minHeight: "250px" }}
              placeholder="Nhập mô tả loại dịch vụ..."
            />
            {errors.detail && <p className="text-red-500 text-sm mt-1">{errors.detail}</p>}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AddCategory;
